export const scrollIntoViewWithOffset = (element, offset) => {
  window.scrollTo({
    behavior: "smooth",
    top:
      element.getBoundingClientRect().top -
      document.body.getBoundingClientRect().top -
      offset,
  });
};

/**
 * Scrollable parents of `el` (Shopify / nested layouts may scroll a div, not `window`).
 */
function getScrollableAncestors(el) {
  const out = [];
  if (!el) {
    return out;
  }
  let node = el instanceof Element ? el : null;
  while (node) {
    if (node === document.body || node === document.documentElement) {
      break;
    }
    const style = getComputedStyle(node);
    const overflowY = style.overflowY;
    const overflow = style.overflow;
    const yScrollable = /(auto|scroll|overlay)/.test(overflowY);
    const allScrollable = /(auto|scroll|overlay)/.test(overflow);
    if (
      (yScrollable || allScrollable) &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      out.push(node);
    }
    node = node.parentElement;
  }
  return out;
}

/**
 * Move the “page” to the very top: `window`, `documentElement`, and any overflow scroll
 * ancestors (e.g. main content in embedded themes). Safe to call multiple times.
 */
export function scrollPageToTop(contextEl) {
  const run = () => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch (e) {
      window.scrollTo(0, 0);
    }
    const se = document.scrollingElement;
    if (se) {
      se.scrollTop = 0;
    }
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
    const seeds = [contextEl, document.getElementById("app")].filter(Boolean);
    const main =
      document.getElementById("MainContent") || document.getElementById("main");
    if (main) {
      seeds.push(main);
    }
    const seen = new Set();
    seeds.forEach((el) => {
      getScrollableAncestors(el).forEach((node) => {
        if (!seen.has(node)) {
          seen.add(node);
          node.scrollTop = 0;
        }
      });
    });
  };
  run();
  return run;
}

/** Anchor placed in `SustainabilityReport2025.vue` directly under the hero (before section content). */
export const SUSTAINABILITY_REPORT_CONTENT_START_ID =
  "sustainability-report-content-start";

/** Aligned with `.sustainability-report-2025__content-start { scroll-margin-top }` in the report view. */
const SUSTAINABILITY_CONTENT_START_NUDGE_PX = 200;

/**
 * Scroll so the first line of report content (below Sustainability hero + subnav) sits at the top
 * of the viewport — not the absolute page top. Falls back to hero bottom, then `fallbackOffsetPx`.
 */
export function scrollToSustainabilityReportBelowHero(options = {}) {
  const { fallbackOffsetPx = 700 } = options;
  const run = () => {
    const anchor = document.getElementById(
      SUSTAINABILITY_REPORT_CONTENT_START_ID
    );
    if (anchor) {
      anchor.scrollIntoView({
        behavior: "auto",
        block: "start",
        inline: "nearest",
      });
      return;
    }
    const hero = document.querySelector(".sustainability-hero-2025");
    let y = Math.max(
      0,
      fallbackOffsetPx - SUSTAINABILITY_CONTENT_START_NUDGE_PX
    );
    if (hero) {
      y = Math.max(
        0,
        window.pageYOffset +
          hero.getBoundingClientRect().bottom -
          SUSTAINABILITY_CONTENT_START_NUDGE_PX
      );
    }
    try {
      window.scrollTo({ top: y, left: 0, behavior: "auto" });
    } catch (e) {
      window.scrollTo(0, y);
    }
    const se = document.scrollingElement;
    if (se) {
      se.scrollTop = y;
    }
    if (document.documentElement) {
      document.documentElement.scrollTop = y;
    }
    if (document.body) {
      document.body.scrollTop = y;
    }
  };
  run();
  return run;
}
